import { Input } from "@/modern/components/ui/input";

import { cn } from "@/modern/utils";
import React from "react";
import {
  Period,
  TimePickerType,
  getArrowByType,
  getDateByType,
  setDateByType,
  display12HourValue
} from "./time-picker-utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/modern/components/ui/select";

export interface TimePickerInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  picker: TimePickerType;
  date: Date | undefined;
  setDate: (date: Date | undefined) => void;
  period?: Period;
  onRightFocus?: () => void;
  onLeftFocus?: () => void;
}

const TimePickerInput = React.forwardRef<
  HTMLInputElement,
  TimePickerInputProps
>(
  (
    {
      className,
      type = "tel",
      value,
      id,
      name,
      date = new Date(new Date().setHours(0, 0, 0, 0)),
      setDate,
      onChange,
      onKeyDown,
      picker,
      period,
      onLeftFocus,
      onRightFocus,
      ...props
    },
    ref
  ) => {
    const [flag, setFlag] = React.useState<boolean>(false);
    const [prevIntKey, setPrevIntKey] = React.useState<string>("0");

    /**
     * allow the user to enter the second digit within 2 seconds
     * otherwise start again with entering first digit
     */
    React.useEffect(() => {
      if (flag) {
        const timer = setTimeout(() => {
          setFlag(false);
        }, 2000);

        return () => clearTimeout(timer);
      }
      return () => { };
    }, [flag]);

    const calculatedValue = React.useMemo(() => {
      return getDateByType(date, picker);
    }, [date, picker]);

    const calculateNewValue = (key: string) => {
      /*
       * If picker is '12hours' and the first digit is 0, then the second digit is automatically set to 1.
       * The second entered digit will break the condition and the value will be set to 10-12.
       */
      if (picker === "12hours") {
        if (flag && calculatedValue.slice(1, 2) === "1" && prevIntKey === "0")
          return "0" + key;
      }

      return !flag ? "0" + key : calculatedValue.slice(1, 2) + key;
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Tab") return;
      e.preventDefault();
      if (e.key === "ArrowRight") onRightFocus?.();
      if (e.key === "ArrowLeft") onLeftFocus?.();
      if (["ArrowUp", "ArrowDown"].includes(e.key)) {
        const step = e.key === "ArrowUp" ? 1 : -1;
        const newValue = getArrowByType(calculatedValue, step, picker);
        if (flag) setFlag(false);
        const tempDate = new Date(date);
        setDate(setDateByType(tempDate, newValue, picker, period));
      }
      if (e.key >= "0" && e.key <= "9") {
        if (picker === "12hours") setPrevIntKey(e.key);

        const newValue = calculateNewValue(e.key);
        if (flag) onRightFocus?.();
        setFlag((prev) => !prev);
        const tempDate = new Date(date);
        setDate(setDateByType(tempDate, newValue, picker, period));
      }
    };

    return (
      <Input
        ref={ref}
        id={id || picker}
        name={name || picker}
        className={cn(
          "bg-transparent shadow-none border-none outline-none",
          "text-center w-12 h-10 px-2 text-base font-medium tabular-nums",
          "focus-visible:text-primary focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none",
          "transition-all duration-150",
          className
        )}
        value={value || calculatedValue}
        onChange={(e) => {
          e.preventDefault();
          onChange?.(e);
        }}
        type={type}
        inputMode="decimal"
        onKeyDown={(e) => {
          onKeyDown?.(e);
          handleKeyDown(e);
        }}
        {...props}
      />
    );
  }
);

TimePickerInput.displayName = "TimePickerInput";


export interface PeriodSelectorProps {
  period: Period;
  setPeriod: (m: Period) => void;
  date: Date | undefined;
  setDate: (date: Date | undefined) => void;
  onRightFocus?: () => void;
  onLeftFocus?: () => void;
  disabled?: boolean;
}

const TimePeriodSelect = React.forwardRef<HTMLButtonElement, PeriodSelectorProps>(({ period, setPeriod, date, setDate, onLeftFocus, onRightFocus, disabled }, ref) => {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "ArrowRight") onRightFocus?.();
    if (e.key === "ArrowLeft") onLeftFocus?.();
  };

  const handleValueChange = (value: Period) => {
    setPeriod(value);

    /**
     * trigger an update whenever the user switches between AM and PM;
     * otherwise user must manually change the hour each time
     */
    if (date) {
      const tempDate = new Date(date);
      const hours = display12HourValue(date.getHours());
      setDate(setDateByType(tempDate, hours.toString(), "12hours", period === "AM" ? "PM" : "AM"));
    }
  };

  return (
    <Select value={period} onValueChange={(value: Period) => handleValueChange(value)} disabled={disabled}>
      <SelectTrigger
        ref={ref}
        className={cn(
          "h-10 w-20 text-base font-medium bg-transparent border-none",
          "focus-visible:text-primary focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none",
          "transition-all duration-150"
        )}
        onKeyDown={handleKeyDown}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="AM">AM</SelectItem>
        <SelectItem value="PM">PM</SelectItem>
      </SelectContent>
    </Select>
  );
});

TimePeriodSelect.displayName = "TimePeriodSelect";


interface TimePickerProps {
  date: Date | undefined;
  onChange: (date: Date | undefined) => boolean | void;
  className?: string;
  disabled?: boolean;
}

export function TimePicker({ date: initialDate, onChange, className, disabled }: TimePickerProps) {
  const [period, setPeriod] = React.useState<Period>("PM");
  const [date, setDate] = React.useState<Date | undefined>(initialDate);
  const [hasChanges, setHasChanges] = React.useState(false);

  const minuteRef = React.useRef<HTMLInputElement>(null);
  const hourRef = React.useRef<HTMLInputElement>(null);
  const secondRef = React.useRef<HTMLInputElement>(null);
  const periodRef = React.useRef<HTMLButtonElement>(null);

  // Update local state when initialDate changes from parent
  React.useEffect(() => {
    setDate(initialDate);
    setHasChanges(false);
  }, [initialDate]);

  // Track if date has changed
  React.useEffect(() => {
    if (!initialDate && !date) {
      setHasChanges(false);
      return;
    }

    if (!initialDate || !date) {
      setHasChanges(true);
      return;
    }

    const hasChanged =
      initialDate.getHours() !== date.getHours() ||
      initialDate.getMinutes() !== date.getMinutes();

    setHasChanges(hasChanged);
  }, [date, initialDate]);

  const handleSetDate = (newDate: Date | undefined) => {
    setDate(newDate);
  };

  const handleSave = () => {
    const isApplied = onChange(date);
    if (isApplied === false) {
      // Validation failed, revert to initial date
      setDate(initialDate);
      setHasChanges(false);
    } else {
      // Success, hide the save/cancel buttons
      setHasChanges(false);
    }
  };

  const handleCancel = () => {
    setDate(initialDate);
    setHasChanges(false);
  };

  return (
    <div className={cn("relative", className)}>
      <div className={cn("flex items-center gap-2 bg-background border rounded-md w-max", disabled && "opacity-50")}>
        {/* Hours */}
        <TimePickerInput
          disabled={disabled}
          picker="12hours"
          period={period}
          date={date}
          setDate={handleSetDate}
          ref={hourRef}
          onRightFocus={() => minuteRef.current?.focus()}
        />

        {/* Separator */}
        <div className="flex items-center h-10 pb-0.5">
          <span className="text-muted-foreground font-bold text-base">:</span>
        </div>


        <TimePickerInput
          disabled={disabled}
          picker="minutes"
          id="minutes12"
          date={date}
          setDate={handleSetDate}
          ref={minuteRef}
          onLeftFocus={() => hourRef.current?.focus()}
          onRightFocus={() => secondRef.current?.focus()}
        />
        <TimePeriodSelect
          disabled={disabled}
          period={period}
          setPeriod={setPeriod}
          date={date}
          setDate={handleSetDate}
          ref={periodRef}
          onLeftFocus={() => secondRef.current?.focus()}
        />
      </div>

      {/* Floating Save/Cancel Buttons */}
      {hasChanges && !disabled && (
        <div className="absolute top-1 right-full mr-2 flex items-center gap-1 bg-background border rounded-md shadow-lg p-1 z-10 animate-in fade-in slide-in-from-right-1 duration-200">
          <button
            onClick={handleSave}
            className="flex items-center justify-center h-7 px-2 text-xs font-medium text-white bg-primary hover:bg-primary/90 rounded transition-colors"
          >
            <i className="fa fa-check" />
          </button>
          <button
            onClick={handleCancel}
            className="flex items-center justify-center h-7 px-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors"
          >
            <i className="fa fa-times" />
          </button>
        </div>
      )}
    </div>
  );
}

export { TimePickerInput, TimePeriodSelect };